// Crossfades a full-bleed hero through a shuffled list of image URLs supplied as JSON by Liquid.
// Autoplay pauses for reduced motion, hidden tabs, and the explicit pause button (sticky until play).
class HeroRotator extends HTMLElement {
  #abort = null;
  #timer = 0;
  #slides = [];
  #index = 0;
  #layers = [];
  #active = 0;
  #explicitPause = false;
  #reducedMotion = false;
  #hidden = false;
  #busy = false;

  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    this.#layers = Array.from(this.querySelectorAll('[data-layer]'));
    if (!data || this.#layers.length < 2) return;

    let list = [];
    try {
      list = JSON.parse(data.textContent);
    } catch {
      return;
    }
    // Entries are image URLs, or { src, key } objects whose key names what the slide shows (announced on change).
    const entries = Array.isArray(list)
      ? list.map((entry) => (typeof entry === 'string' ? { src: entry } : entry)).filter((entry) => entry && entry.src)
      : [];
    const firstSrc = this.#layers[0].dataset.src;
    const firstEntry = entries.find((entry) => entry.src === firstSrc) || { src: firstSrc };
    if (entries.length < 2) {
      this.#toggleButton()?.setAttribute('hidden', '');
      this.#announce(firstEntry);
      return;
    }

    const rest = entries.filter((entry) => entry.src !== firstSrc);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    this.#slides = [firstEntry, ...rest];
    this.#announce(firstEntry);

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion = motionQuery.matches;
    motionQuery.addEventListener('change', (event) => {
      this.#reducedMotion = event.matches;
      this.#sync();
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      this.#hidden = document.hidden;
      this.#sync();
    }, { signal });

    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle') return;
      this.#explicitPause = !this.#explicitPause;
      this.#sync();
    }, { signal });

    this.#warm(signal);
    this.#sync();
    // Open on a random photo each visit: the server always renders the first slide, so swap once right away.
    if ('randomStart' in this.dataset) this.#advance(true);
  }

  // Once the page has finished loading, fetch the next few slides at low priority so early crossfades are instant.
  #warm(signal) {
    const preload = () => {
      for (const { src } of this.#slides.slice(1, 4)) {
        const img = new Image();
        img.decoding = 'async';
        img.fetchPriority = 'low';
        img.src = this.#urlFor(src);
      }
    };
    if (document.readyState === 'complete') preload();
    else window.addEventListener('load', preload, { once: true, signal });
  }

  // Publishes the visible slide's key: kept on data-current-key for late listeners, and sent as a "rotator:change" event.
  #announce(entry) {
    if (entry?.key === undefined || entry.key === null) return;
    const key = String(entry.key);
    this.dataset.currentKey = key;
    this.dispatchEvent(new CustomEvent('rotator:change', { detail: { key } }));
  }

  disconnectedCallback() {
    this.#abort?.abort();
    window.clearTimeout(this.#timer);
  }

  // The pause button lives beside this element (above the scrim) and targets it via commandfor.
  #toggleButton() {
    if (!this.id) return null;
    return this.parentElement?.querySelector(`[data-toggle][commandfor="${this.id}"]`) ?? null;
  }

  get #paused() {
    return this.#explicitPause || this.#reducedMotion || this.#hidden;
  }

  #sync() {
    window.clearTimeout(this.#timer);
    const button = this.#toggleButton();
    if (button) {
      const playing = !this.#explicitPause && !this.#reducedMotion;
      button.setAttribute('aria-pressed', String(!playing));
      button.setAttribute('aria-label', playing ? button.dataset.labelPause : button.dataset.labelPlay);
      button.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
      button.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
    }
    if (!this.#paused) this.#schedule();
  }

  #schedule() {
    const interval = Number(this.dataset.interval) || 6000;
    this.#timer = window.setTimeout(() => this.#advance(), interval);
  }

  #urlFor(src) {
    const max = Number(this.dataset.maxWidth) || Infinity;
    const width = Math.min(window.innerWidth < 800 ? 1000 : 1920, max);
    return src.replace(/([?&])width=\d+/, `$1width=${width}`);
  }

  // instant: swap without the crossfade (used once on load when data-random-start is set).
  async #advance(instant = false) {
    if (this.#busy || (this.#paused && !instant)) return;
    window.clearTimeout(this.#timer);
    this.#busy = true;
    this.#index = (this.#index + 1) % this.#slides.length;
    const incoming = this.#layers[1 - this.#active];
    const outgoing = this.#layers[this.#active];
    // A server-rendered srcset would override the new src, so drop it before swapping.
    incoming.removeAttribute('srcset');
    incoming.src = this.#urlFor(this.#slides[this.#index].src);
    try {
      await incoming.decode();
    } catch {
      // A failed image is skipped; the current slide stays visible.
      this.#busy = false;
      if (!this.#paused) this.#schedule();
      return;
    }
    if (instant) {
      incoming.style.transitionDuration = '0s';
      outgoing.style.transitionDuration = '0s';
    }
    incoming.classList.replace('opacity-0', 'opacity-100');
    outgoing.classList.replace('opacity-100', 'opacity-0');
    if (instant) {
      // Restore the crossfade once the instant swap has painted.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        incoming.style.transitionDuration = '';
        outgoing.style.transitionDuration = '';
      }));
    }
    this.#active = 1 - this.#active;
    this.#announce(this.#slides[this.#index]);
    this.#busy = false;
    if (!this.#paused) this.#schedule();
  }
}

if (!customElements.get('s-hero-rotator')) customElements.define('s-hero-rotator', HeroRotator);
